import { ApolloProvider } from '@apollo/client';
import { ErrorBoundary } from '@sentry/react';
import { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';
import 'supertokens-auth-react';
import {
  SessionAuth,
  useSessionContext,
} from 'supertokens-auth-react/recipe/session';

import {
  BrowserSessionProvider,
  apolloClientFactory,
  defaultCache,
  sessionAccessTokenProvider,
} from '@eluve/apollo-client';
import { TaskProcessorProviderWrapper } from '@eluve/appointment-tasks';
import { LoadingIndicator } from '@eluve/components';
import {
  PatientAppointmentsList,
  PatientArtifacts,
  PatientDetailsImportedNotes,
  PatientDetailsPageLayout,
  PatientsListPage,
} from '@eluve/feature-patients';
import {
  AppointmentDetailsOutput,
  AppointmentDetailsPage,
  AppointmentPdfPage,
} from '@eluve/frontend-feature-appointment';
import { EHRSyncStoreProvider } from '@eluve/frontend-feature-ehr-sync';
import {
  TemplatesGuard,
  TenantOutputTemplatesListPage,
  outputTemplateRoutes,
} from '@eluve/frontend-feature-output-templates';
import { PromptsPage } from '@eluve/frontend-feature-prompt-templates';
import { tenantAdminRoutes } from '@eluve/frontend-feature-tenants';
import { createReviewRoutes } from '@eluve/frontend-review-dash';
import { Logger } from '@eluve/logger';
import {
  AnalyticsProvider,
  SessionlessAnalyticsProvider,
  UrlParamNormalizer,
} from '@eluve/posthog-react';
import { SessionContextProvider } from '@eluve/session-helpers';
import { SiteBannerProvider } from '@eluve/site-banners';
import {
  FeatureFlaggedPage,
  TenantOrUserFeatureFlaggedComponent,
  UserFeatureFlaggedComponent,
} from '@eluve/smart-blocks';
import {
  AppointmentFilesList,
  AppointmentRecordingStatusStoreProvider,
} from '@eluve/user-local-files';

import { IntercomProvider } from './IntercomProvider';
import { createAdminRoutes } from './admin-routes';
import { appConfig } from './config';
import { ErrorFallbackRender } from './routes/ErrorFallbackRender';
import { ImpersonationLoginPage } from './routes/ImpersonationLoginPage';
import { LoginCallbackPage } from './routes/LoginCallbackPage';
import { LoginPage } from './routes/LoginPage';
import { LogoutPage } from './routes/Logout';
import { MagicLinkVerifyPage } from './routes/MagicLinkVerifyPage';
import { NotFoundPage } from './routes/NotFoundPage';
import { SsoPage } from './routes/SsoPage';
import { WaitListPage } from './routes/WaitlistPage';
import { AppIndexPage } from './routes/app/AppIndexPage';
import { AppLayout } from './routes/app/AppLayout';
import { CompatibilityChecker } from './routes/app/CompatibilityChecker';
import { RemoveSegmentRedirect } from './routes/app/RemoveSegmentRedirect';
import { SingularEntityRedirect } from './routes/app/SingularEntityRedirect';
import { TenantToOrganizationRedirect } from './routes/app/TenantToOrganizationRedirect';
import { UserContextProvider } from './routes/app/UserContextProvider';
import { VersionChecker } from './routes/app/VersionChecker';
import { createEvalRoutes } from './routes/app/eval/eval-routes';
import { createPatientRoutes } from './routes/app/patient/patient-routes';
import { PromptsGuard } from './routes/app/prompts/PromptsGuard';
import { SettingsPage } from './routes/app/settings/SettingsPage';
import { HomePage } from './routes/app/tenant/$tenantId/HomePage';
import { InterstitialsWrapper } from './routes/app/tenant/$tenantId/InterstitialsWrapper';
import { PeriodicFileSyncProviderBridge } from './routes/app/tenant/$tenantId/PeriodicFileSyncProviderBridge';
import { TenantAdminGuard } from './routes/app/tenant/$tenantId/TenantAdminGuard';
import { TenantGuard } from './routes/app/tenant/$tenantId/TenantGuard';
import { TenantLayout } from './routes/app/tenant/$tenantId/TenantLayout';
import { TenantProvider } from './routes/app/tenant/$tenantId/TenantProvider';
import { TenantSettingsMainLayout } from './routes/app/tenant/$tenantId/TenantSettingsMainLayout';
import { AppointmentPage } from './routes/app/tenant/$tenantId/appointments/Appointment.Page';
import { AppointmentDialogProvider } from './routes/app/tenant/$tenantId/appointments/AppointmentDialogProvider';
import { AppointmentsListPage } from './routes/app/tenant/$tenantId/appointments/AppointmentsListPage';
import { AppointmentsMainLayout } from './routes/app/tenant/$tenantId/appointments/AppointmentsMainLayout';
import { SummaryComparisonPage } from './routes/app/tenant/$tenantId/appointments/SummaryComparison.Page';
import { TranscriptionMachineProvider } from './routes/app/tenant/$tenantId/appointments/machines/transcription/TranscriptionMachineProvider';
import { TenantUserChatSettingsPage } from './routes/app/tenant/$tenantId/settings/TenantUserChatSettingsPage';
import { TenantUserSettingsPage } from './routes/app/tenant/$tenantId/settings/TenantUserSettingsPage';
import { PendingApprovalPage } from './routes/app/tenant/PendingApprovalPage';
import { TenantsPage } from './routes/app/tenant/TenantsPage';

const rootApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  cacheInstance: defaultCache,
  accessTokenProvider: sessionAccessTokenProvider({
    sessionProvider: new BrowserSessionProvider(),
    logger: new Logger('root-apollo-client'),
  }).tokenProvider,
});

const reviewerApolloClient = apolloClientFactory({
  uri: appConfig.VITE_APOLLO_CLIENT_URI,
  desiredRole: 'reviewer',
  cacheInstance: defaultCache,
  accessTokenProvider: sessionAccessTokenProvider({
    sessionProvider: new BrowserSessionProvider(),
    logger: new Logger('reviewer-apollo-client'),
  }).tokenProvider,
});

export const reactRouter = createBrowserRouter([
  {
    path: '/login',
    element: (
      <UrlParamNormalizer>
        <SessionlessAnalyticsProvider
          apiKey={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
          apiHost={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_HOST}
        >
          <LoginPage />
        </SessionlessAnalyticsProvider>
      </UrlParamNormalizer>
    ),
  },
  {
    path: '/sign-out',
    element: (
      <SessionlessAnalyticsProvider
        apiKey={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
        apiHost={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_HOST}
      >
        <LogoutPage />
      </SessionlessAnalyticsProvider>
    ),
  },
  {
    path: '/login/callback/*',
    element: <LoginCallbackPage />,
  },
  {
    path: '/impersonate/callback',
    element: (
      <SessionAuth>
        <ImpersonationLoginPage />
      </SessionAuth>
    ),
  },
  {
    path: '/sso',
    element: <SsoPage />,
  },
  {
    path: '/waitlist',
    element: <WaitListPage />,
  },
  {
    path: '/auth/verify',
    element: <MagicLinkVerifyPage />,
  },
  {
    path: '/',
    element: (
      <ErrorBoundary showDialog={true} fallback={ErrorFallbackRender}>
        <SessionAuth>
          <SessionContextProvider useSessionContext={useSessionContext}>
            <AnalyticsProvider
              apiKey={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
              apiHost={appConfig.VITE_REACT_APP_PUBLIC_POSTHOG_HOST}
            >
              <UserContextProvider>
                <CompatibilityChecker>
                  <VersionChecker dismissDelayHours={24} intervalMinutes={5}>
                    <ApolloProvider client={rootApolloClient}>
                      <IntercomProvider>
                        <SiteBannerProvider>
                          <AppLayout />
                        </SiteBannerProvider>
                      </IntercomProvider>
                    </ApolloProvider>
                  </VersionChecker>
                </CompatibilityChecker>
              </UserContextProvider>
            </AnalyticsProvider>
          </SessionContextProvider>
        </SessionAuth>
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <AppIndexPage />,
      },
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      {
        path: 'tenant/*',
        element: <TenantToOrganizationRedirect />,
      },
      {
        path: 'tenants/*',
        element: <TenantToOrganizationRedirect />,
      },
      {
        path: 'organization/*',
        element: <SingularEntityRedirect entityName="organization" />,
      },
      {
        path: 'organizations/switch/*',
        element: <RemoveSegmentRedirect segment="switch" />,
      },
      {
        path: 'organizations',
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <TenantsPage />,
          },
          {
            path: 'pending-approval',
            element: <PendingApprovalPage />,
          },
          {
            path: ':tenantId',
            element: (
              <TenantGuard>
                <Suspense fallback={<LoadingIndicator />}>
                  <EHRSyncStoreProvider
                    initializer={(set) => ({
                      lastSuccessImportCompletedAt: undefined,
                      isDataImporting: undefined,
                      setIsDataImporting: (isLoading) =>
                        set({ isDataImporting: isLoading }),
                    })}
                  >
                    <TenantProvider>
                      <InterstitialsWrapper>
                        <AppointmentRecordingStatusStoreProvider
                          initializer={() => ({
                            isActivelyRecording: false,
                          })}
                        >
                          <TaskProcessorProviderWrapper>
                            <PeriodicFileSyncProviderBridge>
                              <TranscriptionMachineProvider>
                                <AppointmentDialogProvider>
                                  <TenantLayout />
                                </AppointmentDialogProvider>
                              </TranscriptionMachineProvider>
                            </PeriodicFileSyncProviderBridge>
                          </TaskProcessorProviderWrapper>
                        </AppointmentRecordingStatusStoreProvider>
                      </InterstitialsWrapper>
                    </TenantProvider>
                  </EHRSyncStoreProvider>
                </Suspense>
              </TenantGuard>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="home" replace />,
              },
              {
                path: 'home',
                element: <HomePage />,
              },
              {
                path: 'settings',
                element: <TenantUserSettingsPage />,
              },
              {
                path: 'chat-settings',
                element: (
                  <UserFeatureFlaggedComponent flag="CHAT_PERSONALIZATION">
                    <TenantUserChatSettingsPage />
                  </UserFeatureFlaggedComponent>
                ),
              },
              {
                path: 'admin',
                element: (
                  <TenantAdminGuard>
                    <Helmet>
                      <title>Admin | Eluve</title>
                    </Helmet>
                    <Outlet />
                  </TenantAdminGuard>
                ),
                children: [
                  {
                    index: true,
                    element: <Navigate to="general" replace />,
                  },
                  {
                    element: <TenantSettingsMainLayout />,
                    children: tenantAdminRoutes,
                  },
                ],
              },
              {
                path: 'files',
                element: (
                  <FeatureFlaggedPage flag="LOCAL_FILES_MANAGEMENT">
                    <Helmet>
                      <title>Files | Eluve</title>
                    </Helmet>
                    <AppointmentFilesList />
                  </FeatureFlaggedPage>
                ),
              },
              {
                path: 'patient/*',
                element: <SingularEntityRedirect entityName="patient" />,
              },
              {
                path: 'appointments',
                element: (
                  <FeatureFlaggedPage flag="APPOINTMENTS_VIEW">
                    <Helmet>
                      <title>Appointments | Eluve</title>
                    </Helmet>
                    <AppointmentsListPage />
                  </FeatureFlaggedPage>
                ),
              },
              {
                path: 'patients',
                element: (
                  <FeatureFlaggedPage flag="PATIENTS_VIEW">
                    <PatientsListPage />
                  </FeatureFlaggedPage>
                ),
              },
              {
                path: 'patients/:patientId',
                element: (
                  <FeatureFlaggedPage flag="PATIENTS_VIEW">
                    <PatientDetailsPageLayout
                      baseUrl={appConfig.VITE_API_DOMAIN}
                    />
                  </FeatureFlaggedPage>
                ),
                children: [
                  {
                    index: true,
                    element: <Navigate to="appointments" replace />,
                  },
                  {
                    path: 'artifacts',
                    element: (
                      <TenantOrUserFeatureFlaggedComponent flag="INSIGHTS_CHAT">
                        <PatientArtifacts />
                      </TenantOrUserFeatureFlaggedComponent>
                    ),
                  },
                  {
                    path: 'appointments',
                    element: <PatientAppointmentsList />,
                  },
                  {
                    path: 'imported-notes',
                    element: <PatientDetailsImportedNotes />,
                  },
                ],
              },
              {
                path: 'output-templates',
                element: (
                  <TemplatesGuard>
                    <Helmet>
                      <title>Templates | Eluve</title>
                    </Helmet>
                    <Outlet />
                  </TemplatesGuard>
                ),
                children: [
                  {
                    index: true,
                    element: <TenantOutputTemplatesListPage />,
                  },
                  ...outputTemplateRoutes,
                ],
              },

              {
                path: 'appointment/*',
                element: <SingularEntityRedirect entityName="appointment" />,
              },
              {
                path: 'appointments/:appointmentId',
                element: <AppointmentsMainLayout />,
                children: [
                  {
                    index: true,
                    element: <AppointmentPage />,
                  },
                  {
                    path: 'comparison',
                    element: <SummaryComparisonPage />,
                  },
                  {
                    path: 'pdf',
                    element: (
                      <FeatureFlaggedPage flag="PDF_OUTPUT">
                        <AppointmentPdfPage />
                      </FeatureFlaggedPage>
                    ),
                  },
                  {
                    path: 'details',
                    element: (
                      <FeatureFlaggedPage flag="LLM_OUTPUT_DETAILS">
                        <AppointmentDetailsPage />
                      </FeatureFlaggedPage>
                    ),
                    children: [
                      {
                        index: true,
                        element: (
                          <div className="text-text-tertiary">
                            Select Output types
                          </div>
                        ),
                      },
                      {
                        path: ':outputType',
                        element: <AppointmentDetailsOutput />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'prompts',
        element: (
          <PromptsGuard>
            <PromptsPage />
          </PromptsGuard>
        ),
      },
      ...createAdminRoutes('admin'),
      ...createEvalRoutes('eval'),
      ...createReviewRoutes('review', reviewerApolloClient),
    ],
  },
  ...createPatientRoutes('/patient-organization', rootApolloClient),
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
